import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTemplate } from '@/contexts/TemplateContext'
import type { PublicProfile } from '@/types'
import { Gallery } from '@/components/gallery'
import { BookingModal } from '@/components/booking/BookingModal'

interface HairStylistTemplateProps {
    profile: PublicProfile
}

export function HairStylistTemplate({ profile }: HairStylistTemplateProps) {
    const { t } = useTranslation('public')
    const { colors, typography } = useTemplate()
    const [isBookingOpen, setIsBookingOpen] = useState(false)

    return (
        <div
            className="min-h-screen"
            style={{
                fontFamily: typography.body,
                backgroundColor: colors.colors.background,
                color: colors.colors.text,
            }}
        >
            {/* Navigation */}
            <nav className="bg-white/90 backdrop-blur-md sticky top-0 z-50 border-b" style={{ borderColor: `${colors.colors.primary}22` }}>
                <div className="max-w-7xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            {profile.profile_image && (
                                <img
                                    src={profile.profile_image}
                                    alt={profile.business_name}
                                    className="w-12 h-12 rounded-full object-cover border-2"
                                    style={{ borderColor: colors.colors.primary }}
                                />
                            )}
                            <div>
                                <h1 className="text-xl font-bold">{profile.business_name}</h1>
                                {profile.tagline && (
                                    <p className="text-xs uppercase tracking-wider" style={{ color: colors.colors.textMuted }}>
                                        {profile.tagline}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="hidden md:flex items-center space-x-8">
                            <a href="#about" className="hover:opacity-80 transition-colors cursor-pointer" style={{ color: colors.colors.textMuted }}>
                                {t('template.beauty.about')}
                            </a>
                            <a href="#services" className="hover:opacity-80 transition-colors cursor-pointer" style={{ color: colors.colors.textMuted }}>
                                {t('template.beauty.services')}
                            </a>
                            <a href="#gallery" className="hover:opacity-80 transition-colors cursor-pointer" style={{ color: colors.colors.textMuted }}>
                                {t('template.beauty.portfolio')}
                            </a>
                            <a href="#contact" className="hover:opacity-80 transition-colors cursor-pointer" style={{ color: colors.colors.textMuted }}>
                                {t('template.beauty.contact')}
                            </a>
                            <button
                                onClick={() => setIsBookingOpen(true)}
                                className="px-5 py-2 rounded-full font-medium hover:opacity-90 transition-colors cursor-pointer"
                                style={{ backgroundColor: colors.colors.primary, color: colors.colors.secondary }}
                            >
                                {t('template.beauty.bookNow')}
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero */}
            <section className="relative min-h-screen flex items-center overflow-hidden" style={{ backgroundColor: colors.colors.background }}>
                <div className="absolute inset-0 opacity-5">
                    <div
                        className="absolute top-20 left-20 w-96 h-96 rounded-full blur-3xl"
                        style={{ backgroundColor: colors.colors.accent }}
                    />
                    <div
                        className="absolute bottom-20 right-20 w-96 h-96 rounded-full blur-3xl"
                        style={{ backgroundColor: colors.colors.primary }}
                    />
                </div>
                <div className="max-w-7xl mx-auto px-4 py-20 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <p
                                className="font-medium tracking-wider uppercase mb-4"
                                style={{ color: colors.colors.accent }}
                            >
                                Award-Winning Hair Stylist
                            </p>
                            <h1
                                className="text-5xl md:text-7xl font-bold leading-tight mb-6"
                                style={{ fontFamily: typography.heading }}
                            >
                                Transform Your Look With{' '}
                                <span style={{ color: colors.colors.accent }}>Precision</span> &{' '}
                                <span style={{ color: colors.colors.accent }}>Passion</span>
                            </h1>
                            <p className="text-xl mb-8 leading-relaxed" style={{ color: colors.colors.textMuted }}>
                                {profile.bio || 'With over 12 years of experience styling for fashion weeks, celebrity events, and discerning clients, I bring editorial expertise to every chair.'}
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <a
                                    href="#contact"
                                    className="inline-flex items-center px-8 py-4 rounded-full font-semibold hover:opacity-90 transition-all cursor-pointer"
                                    style={{ backgroundColor: colors.colors.primary, color: colors.colors.secondary }}
                                >
                                    {t('template.beauty.bookConsultation')}
                                    <span className="ml-2">→</span>
                                </a>
                                <a
                                    href="#gallery"
                                    className="inline-flex items-center px-8 py-4 border-2 rounded-full font-semibold hover:opacity-90 transition-all cursor-pointer"
                                    style={{ borderColor: colors.colors.primary, color: colors.colors.primary }}
                                >
                                    {t('template.beauty.viewPortfolio')}
                                </a>
                            </div>
                            <div className="flex items-center space-x-8 mt-12">
                                <div>
                                    <p className="text-4xl font-bold" style={{ color: colors.colors.primary }}>
                                        500+
                                    </p>
                                    <p className="text-sm" style={{ color: colors.colors.textMuted }}>
                                        {t('template.beauty.happyClients')}
                                    </p>
                                </div>
                                <div className="w-px h-16" style={{ backgroundColor: colors.colors.textMuted }} />
                                <div>
                                    <p className="text-4xl font-bold" style={{ color: colors.colors.primary }}>
                                        12+
                                    </p>
                                    <p className="text-sm" style={{ color: colors.colors.textMuted }}>
                                        {t('template.beauty.yearsExperience')}
                                    </p>
                                </div>
                                <div className="w-px h-16" style={{ backgroundColor: colors.colors.textMuted }} />
                                <div>
                                    <p className="text-4xl font-bold" style={{ color: colors.colors.primary }}>
                                        50+
                                    </p>
                                    <p className="text-sm" style={{ color: colors.colors.textMuted }}>
                                        {t('template.beauty.awardsWon')}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="relative">
                            <div
                                className="absolute inset-0 rounded-3xl transform rotate-3"
                                style={{ backgroundColor: `${colors.colors.accent}33` }}
                            />
                            {profile.cover_image ? (
                                <img
                                    src={profile.cover_image}
                                    alt="Hair Styling Studio"
                                    className="relative rounded-3xl shadow-2xl w-full object-cover"
                                    style={{ aspectRatio: '3/4' }}
                                />
                            ) : profile.profile_image ? (
                                <img
                                    src={profile.profile_image}
                                    alt={profile.business_name}
                                    className="relative rounded-3xl shadow-2xl w-full object-cover"
                                    style={{ aspectRatio: '3/4' }}
                                />
                            ) : (
                                <div
                                    className="relative rounded-3xl shadow-2xl w-full flex items-center justify-center"
                                    style={{ backgroundColor: colors.colors.secondary, aspectRatio: '3/4' }}
                                >
                                    <span className="text-6xl">💇</span>
                                </div>
                            )}
                            <div
                                className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6"
                                style={{ backgroundColor: colors.colors.background }}
                            >
                                <div className="flex items-center space-x-4">
                                    <div className="flex -space-x-3">
                                        {[1, 2, 3].map((i) => (
                                            <div
                                                key={i}
                                                className="w-10 h-10 rounded-full border-2 border-white object-cover"
                                                style={{ backgroundColor: colors.colors.secondary }}
                                            />
                                        ))}
                                    </div>
                                    <div>
                                        <p className="font-bold">5.0 Rating</p>
                                        <div className="flex text-sm" style={{ color: colors.colors.accent }}>
                                            ★★★★★
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* About */}
            <section id="about" className="py-24 px-4 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div className="relative">
                            {profile.profile_image ? (
                                <img
                                    src={profile.profile_image}
                                    alt={profile.business_name}
                                    className="rounded-3xl shadow-2xl w-full object-cover"
                                />
                            ) : (
                                <div
                                    className="rounded-3xl shadow-2xl w-full flex items-center justify-center"
                                    style={{ backgroundColor: colors.colors.secondary, aspectRatio: '3/4' }}
                                >
                                    <span className="text-6xl">💇</span>
                                </div>
                            )}
                            <div
                                className="absolute -top-6 -right-6 rounded-2xl p-6 shadow-xl"
                                style={{ backgroundColor: colors.colors.primary, color: colors.colors.secondary }}
                            >
                                <p className="text-4xl font-bold">12+</p>
                                <p className="text-sm" style={{ color: colors.colors.secondary }}>{t('template.beauty.yearsInIndustry')}</p>
                            </div>
                        </div>
                        <div>
                            <p
                                className="font-medium tracking-wider uppercase mb-2"
                                style={{ color: colors.colors.accent }}
                            >
                                {t('template.beauty.aboutMe')}
                            </p>
                            <h2
                                className="text-4xl md:text-5xl font-bold mb-6"
                                style={{ fontFamily: typography.heading }}
                            >
                                Your Style, <br />My Passion
                            </h2>
                            {profile.bio && (
                                <p className="text-lg mb-6 leading-relaxed" style={{ color: colors.colors.textMuted }}>
                                    {profile.bio}
                                </p>
                            )}
                            {profile.location && (
                                <p className="text-sm mb-8" style={{ color: colors.colors.textMuted }}>
                                    📍 {profile.location}
                                </p>
                            )}
                            <div className="grid grid-cols-2 gap-6 mb-8">
                                <div
                                    className="rounded-2xl p-5"
                                    style={{ backgroundColor: colors.colors.background }}
                                >
                                    <span className="text-2xl mb-3 block" style={{ color: colors.colors.accent }}>
                                        🎓
                                    </span>
                                    <h4 className="font-bold">{t('template.beauty.education')}</h4>
                                    <p className="text-sm" style={{ color: colors.colors.textMuted }}>
                                        Professional Training<br />Advanced Color Training
                                    </p>
                                </div>
                                <div
                                    className="rounded-2xl p-5"
                                    style={{ backgroundColor: colors.colors.background }}
                                >
                                    <span className="text-2xl mb-3 block" style={{ color: colors.colors.accent }}>
                                        🏆
                                    </span>
                                    <h4 className="font-bold">{t('template.beauty.certifications')}</h4>
                                    <p className="text-sm" style={{ color: colors.colors.textMuted }}>
                                        Balayage Specialist<br />Creative Color Expert
                                    </p>
                                </div>
                            </div>
                            {profile.social_links && profile.social_links.length > 0 && (
                                <div className="flex items-center space-x-4">
                                    {profile.social_links.slice(0, 3).map((link) => (
                                        <a
                                            key={link.platform}
                                            href={link.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={`Follow on ${link.platform}`}
                                            className="w-12 h-12 rounded-full flex items-center justify-center transition-all hover:opacity-80"
                                            style={{ backgroundColor: colors.colors.background, color: colors.colors.primary }}
                                        >
                                            {link.platform === 'instagram' && '📷'}
                                            {link.platform === 'facebook' && '📘'}
                                            {link.platform === 'tiktok' && '🎵'}
                                            {link.platform === 'youtube' && '📺'}
                                            {!['instagram', 'facebook', 'tiktok', 'youtube'].includes(link.platform) && '🔗'}
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Services */}
            {profile.services && profile.services.length > 0 && (
                <section
                    id="services"
                    className="py-24 px-4"
                    style={{ backgroundColor: colors.colors.background }}
                >
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-16">
                            <p
                                className="font-medium tracking-wider uppercase mb-2"
                                style={{ color: colors.colors.accent }}
                            >
                                {t('template.beauty.whatIOffer')}
                            </p>
                            <h2
                                className="text-4xl md:text-5xl font-bold"
                                style={{ fontFamily: typography.heading }}
                            >
                                {t('template.beauty.servicesAndPricing')}
                            </h2>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {profile.services.map((service) => (
                                <div
                                    key={service.id}
                                    className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow border group"
                                    style={{ borderColor: `${colors.colors.primary}11` }}
                                >
                                    <div className="flex items-center justify-between mb-6">
                                        <div
                                            className="w-16 h-16 rounded-2xl flex items-center justify-center transition-colors"
                                            style={{ backgroundColor: colors.colors.background }}
                                        >
                                            <span style={{ color: colors.colors.primary }}>✂️</span>
                                        </div>
                                        {service.price_display !== 'hidden' && service.price && (
                                            <span className="text-3xl font-bold" style={{ color: colors.colors.primary }}>
                                                {service.price_display === 'from' && '$'}
                                                {service.price_display === 'from' ? `${service.price.toFixed(0)}+` : `$${service.price.toFixed(0)}`}
                                            </span>
                                        )}
                                    </div>
                                    <h3 className="text-2xl font-bold mb-3">{service.name}</h3>
                                    {service.description && (
                                        <p className="mb-4" style={{ color: colors.colors.textMuted }}>
                                            {service.description}
                                        </p>
                                    )}
                                    {service.duration_minutes && (
                                        <div className="flex items-center text-sm mb-4" style={{ color: colors.colors.textMuted }}>
                                            <span className="mr-2" style={{ color: colors.colors.accent }}>⏱</span>
                                            <span>{service.duration_minutes} {t('template.common.min')}</span>
                                        </div>
                                    )}
                                    <a
                                        href="#contact"
                                        className="font-semibold flex items-center hover:opacity-80 transition-colors cursor-pointer"
                                        style={{ color: colors.colors.accent }}
                                    >
                                        {t('template.beauty.bookNow')} <span className="ml-2">→</span>
                                    </a>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Gallery */}
            {profile.gallery && profile.gallery.length > 0 && (
                <section
                    id="gallery"
                    className="py-24 px-4"
                    style={{ backgroundColor: colors.colors.primary }}
                >
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-16">
                            <p
                                className="font-medium tracking-wider uppercase mb-2"
                                style={{ color: colors.colors.accent }}
                            >
                                {t('template.beauty.portfolio')}
                            </p>
                            <h2
                                className="text-4xl md:text-5xl font-bold"
                                style={{ fontFamily: typography.heading, color: colors.colors.secondary }}
                            >
                                {t('template.beauty.myWork')}
                            </h2>
                        </div>
                        <Gallery
                            images={profile.gallery}
                            layout="grid"
                            limit={8}
                            enableLightbox={true}
                            showCaptions={false}
                            aspectRatio="square"
                            gapSize={4}
                        />
                    </div>
                </section>
            )}

            {/* Hours */}
            {profile.availability && profile.availability.length > 0 && (
                <section className="py-24 px-4 bg-white">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-12">
                            <p
                                className="font-medium tracking-wider uppercase mb-2"
                                style={{ color: colors.colors.accent }}
                            >
                                {t('template.beauty.availability')}
                            </p>
                            <h2
                                className="text-4xl md:text-5xl font-bold"
                                style={{ fontFamily: typography.heading }}
                            >
                                {t('template.beauty.studioHours')}
                            </h2>
                        </div>
                        <div
                            className="rounded-3xl p-8 md:p-12"
                            style={{ backgroundColor: colors.colors.background }}
                        >
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="space-y-4">
                                    {profile.availability.slice(0, 4).map((day) => (
                                        <div
                                            key={day.day_of_week}
                                            className="flex justify-between items-center py-3 border-b"
                                            style={{ borderColor: `${colors.colors.primary}22` }}
                                        >
                                            <span className="font-semibold">{day.day_name}</span>
                                            <span style={{ color: colors.colors.textMuted }}>
                                                {day.is_available
                                                    ? day.is_all_day
                                                        ? t('template.common.allDay')
                                                        : `${day.start_time} - ${day.end_time}`
                                                    : t('template.common.closed')}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                <div className="space-y-4">
                                    {profile.availability.slice(4).map((day) => (
                                        <div
                                            key={day.day_of_week}
                                            className="flex justify-between items-center py-3 border-b"
                                            style={{ borderColor: `${colors.colors.primary}22` }}
                                        >
                                            <span className="font-semibold">{day.day_name}</span>
                                            <span style={{ color: colors.colors.textMuted }}>
                                                {day.is_available
                                                    ? day.is_all_day
                                                        ? t('template.common.allDay')
                                                        : `${day.start_time} - ${day.end_time}`
                                                    : t('template.common.closed')}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div
                                className="mt-8 text-center rounded-2xl p-6"
                                style={{ backgroundColor: colors.colors.background }}
                            >
                                <p style={{ color: colors.colors.textMuted }}>
                                    <span className="mr-2" style={{ color: colors.colors.accent }}>ℹ️</span>
                                    {t('template.beauty.consultationsComplimentary')}
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Contact */}
            <section id="contact" className="py-24 px-4" style={{ backgroundColor: colors.colors.background }}>
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16">
                        <div>
                            <p
                                className="font-medium tracking-wider uppercase mb-2"
                                style={{ color: colors.colors.accent }}
                            >
                                {t('template.beauty.getInTouch')}
                            </p>
                            <h2
                                className="text-4xl md:text-5xl font-bold mb-6"
                                style={{ fontFamily: typography.heading }}
                            >
                                Let's Create Something Beautiful
                            </h2>
                            <p className="text-lg mb-8 leading-relaxed" style={{ color: colors.colors.textMuted }}>
                                Ready for a transformation? Reach out to book your appointment or schedule a complimentary consultation.
                            </p>
                            <div className="space-y-6">
                                {profile.location && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: colors.colors.background }}
                                        >
                                            <span style={{ color: colors.colors.accent }}>📍</span>
                                        </div>
                                        <div>
                                            <h4 className="font-bold">{t('template.beauty.studioLocation')}</h4>
                                            <p style={{ color: colors.colors.textMuted }}>{profile.location}</p>
                                        </div>
                                    </div>
                                )}
                                {profile.phone && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: colors.colors.background }}
                                        >
                                            <span style={{ color: colors.colors.accent }}>📞</span>
                                        </div>
                                        <div>
                                            <h4 className="font-bold">{t('template.beauty.phone')}</h4>
                                            <p style={{ color: colors.colors.textMuted }}>{profile.phone}</p>
                                        </div>
                                    </div>
                                )}
                                {profile.email && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: colors.colors.background }}
                                        >
                                            <span style={{ color: colors.colors.accent }}>✉️</span>
                                        </div>
                                        <div>
                                            <h4 className="font-bold">{t('template.beauty.email')}</h4>
                                            <p style={{ color: colors.colors.textMuted }}>{profile.email}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {profile.social_links && profile.social_links.length > 0 && (
                                <div className="mt-8">
                                    <h4 className="font-bold mb-4">{t('template.beauty.followMyWork')}</h4>
                                    <div className="flex space-x-4">
                                        {profile.social_links.slice(0, 3).map((link) => (
                                            <a
                                                key={link.platform}
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={`Follow on ${link.platform}`}
                                                className="w-14 h-14 rounded-2xl flex items-center justify-center transition-all hover:opacity-80"
                                                style={{ backgroundColor: colors.colors.background, color: colors.colors.primary }}
                                            >
                                                {link.platform === 'instagram' && '📷'}
                                                {link.platform === 'facebook' && '📘'}
                                                {link.platform === 'tiktok' && '🎵'}
                                                {link.platform === 'pinterest' && '📌'}
                                                {!['instagram', 'facebook', 'tiktok', 'pinterest'].includes(link.platform) && '🔗'}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Contact CTA */}
                        <div className="flex flex-col justify-center">
                            <div
                                className="bg-white rounded-3xl p-8 shadow-xl border"
                                style={{ borderColor: `${colors.colors.primary}11` }}
                            >
                                <h3 className="text-2xl font-bold mb-6">{t('template.beauty.bookYourAppointment')}</h3>
                                <div className="flex flex-col gap-3">
                                    {profile.phone && (
                                        <a
                                            href={`tel:${profile.phone}`}
                                            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold text-lg transition-opacity hover:opacity-90"
                                            style={{
                                                backgroundColor: colors.colors.primary,
                                                color: colors.colors.secondary,
                                            }}
                                        >
                                            📞 {t('template.beauty.callNow')}
                                        </a>
                                    )}
                                    {profile.email && (
                                        <a
                                            href={`mailto:${profile.email}`}
                                            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold text-lg transition-opacity hover:opacity-90"
                                            style={{
                                                backgroundColor: colors.colors.accent,
                                                color: colors.colors.primary,
                                            }}
                                        >
                                            ✉️ {t('template.beauty.emailMe')}
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 px-4" style={{ backgroundColor: colors.colors.primary, color: colors.colors.secondary }}>
                <div className="max-w-7xl mx-auto text-center">
                    {profile.profile_image && (
                        <img
                            src={profile.profile_image}
                            alt={profile.business_name}
                            className="w-16 h-16 rounded-full mx-auto mb-4 border-2"
                            style={{ borderColor: colors.colors.accent }}
                        />
                    )}
                    <h3 className="text-2xl font-bold mb-2">{profile.business_name}</h3>
                    {profile.tagline && (
                        <p className="mb-6" style={{ color: colors.colors.secondary }}>
                            {profile.tagline}
                        </p>
                    )}
                    {profile.social_links && profile.social_links.length > 0 && (
                        <div className="flex justify-center space-x-4 mb-6">
                            {profile.social_links.slice(0, 3).map((link) => (
                                <a
                                    key={link.platform}
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={`Follow on ${link.platform}`}
                                    className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:opacity-80"
                                    style={{ backgroundColor: colors.colors.secondary, color: colors.colors.primary }}
                                >
                                    {link.platform === 'instagram' && '📷'}
                                    {link.platform === 'facebook' && '📘'}
                                    {link.platform === 'tiktok' && '🎵'}
                                    {!['instagram', 'facebook', 'tiktok'].includes(link.platform) && '🔗'}
                                </a>
                            ))}
                        </div>
                    )}
                    <p style={{ color: `${colors.colors.secondary}99` }}>
                        © {new Date().getFullYear()} {profile.business_name}. {t('template.beauty.poweredBy')}
                    </p>
                </div>
            </footer>

            {isBookingOpen && profile.services && profile.services.length > 0 && (
                <BookingModal
                    isOpen={isBookingOpen}
                    onClose={() => setIsBookingOpen(false)}
                    profile={profile}
                    services={profile.services}
                    categories={[]}
                />
            )}
        </div>
    )
}

export default HairStylistTemplate;
